import React, { useState, useEffect } from 'react';
import { X, Plus, Globe, Trash2, KeyRound, Eye, EyeOff, Save, Check, ExternalLink, Zap } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;
const authHdr = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

// ── Price Extractor via AI ─────────────────────────────────────────────────────
const PriceExtractor = ({ supplierId, currentPageUrl, onImportPrice }) => {
  const [url, setUrl]         = useState('');
  const [extracting, setExtracting] = useState(false);
  const [prices, setPrices]   = useState([]);
  const [source, setSource]   = useState('');

  // Auto-fill with current iframe URL
  useEffect(() => { if (currentPageUrl) setUrl(currentPageUrl); }, [currentPageUrl]);

  const extract = async () => {
    const target = url.trim() || currentPageUrl;
    if (!target) { toast.error('URL de page produit requise'); return; }
    setExtracting(true); setPrices([]);
    try {
      const r = await fetch(
        `${API_URL}/api/supplier/extract-price?supplier_id=${supplierId}&url=${encodeURIComponent(target)}`,
        { headers: authHdr() }
      );
      if (!r.ok) throw new Error((await r.json()).detail);
      const data = await r.json();
      setPrices(data.prices || []);
      setSource(data.source || '');
      if (!data.prices?.length) toast.info('Aucun prix détecté — naviguez vers une page produit');
      else toast.success(`${data.prices.length} prix détecté(s) !`);
    } catch (e) { toast.error(`Extraction échouée: ${e.message}`); }
    finally { setExtracting(false); }
  };

  return (
    <div className="space-y-2 p-3 bg-green-900/20 border border-green-700/50 rounded-xl">
      <p className="text-green-400 text-xs font-bold flex items-center gap-1.5"><Zap size={11} /> Extraction IA des prix</p>
      <p className="text-green-500 text-xs">Naviguez vers un produit puis cliquez "Extraire"</p>
      <input value={url} onChange={e => setUrl(e.target.value)} placeholder="URL produit..."
        className="w-full bg-gray-800 border border-green-700/50 text-white text-xs rounded-lg px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 placeholder-gray-500" />
      <button onClick={extract} disabled={extracting} data-testid="extract-price-btn"
        className="w-full flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white font-bold py-2 rounded-xl text-xs transition-all">
        {extracting ? <><div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" /> Analyse...</> : <><Zap size={11} /> Extraire les prix</>}
      </button>
      {prices.length > 0 && (
        <div className="space-y-1 mt-1">
          <p className="text-gray-500 text-[10px]">{prices.length} prix — cliquez pour importer :</p>
          {prices.map((p, i) => (
            <button key={i} onClick={() => { onImportPrice?.(p.price); toast.success(`${p.price.toFixed(2)} € importé !`); }}
              className="w-full flex items-center justify-between px-2.5 py-2 bg-gray-800 border border-green-700/50 hover:border-green-500 rounded-lg transition-all text-left">
              <span className="text-gray-300 text-xs truncate flex-1 mr-2">{p.label}</span>
              <span className="shrink-0 text-green-400 font-black text-sm">{p.price.toFixed(2)} € <span className="text-gray-500 text-[10px] font-normal">{p.type}</span></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Credentials Panel ──────────────────────────────────────────────────────────
const CredsPanel = ({ supplierId, supplier, onReload }) => {
  const [username, setUsername] = useState(supplier?.username || '');
  const [password, setPassword] = useState(supplier?.password || '');
  const [showPwd, setShowPwd]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [logging, setLogging]   = useState(false);

  const saveCredentials = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/supplier/suppliers/${supplierId}`, {
        method: 'PUT', headers: authHdr(),
        body: JSON.stringify({ username, password })
      });
      toast.success('Identifiants sauvegardés');
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  const autoLogin = async () => {
    if (!username || !password) { toast.error('Renseignez d\'abord vos identifiants'); return; }
    setLogging(true);
    try {
      const r = await fetch(`${API_URL}/api/supplier/suppliers/${supplierId}/auto-login`, { method: 'POST', headers: authHdr() });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail);
      toast.success(data.message || 'Connexion effectuée — rechargement...');
      setTimeout(() => onReload?.(), 800);
    } catch (e) { toast.error(`Auto-login: ${e.message}`); }
    finally { setLogging(false); }
  };

  return (
    <div className="space-y-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl">
      <p className="text-amber-400 text-xs font-bold flex items-center gap-1.5"><KeyRound size={11} /> Identifiants fournisseur</p>
      <div className="space-y-2">
        <input value={username} onChange={e => setUsername(e.target.value)} placeholder="Email / Identifiant"
          className="w-full bg-gray-800 border border-amber-500/40 text-white text-xs rounded-lg px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500 placeholder-gray-500" />
        <div className="relative">
          <input type={showPwd ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Mot de passe"
            className="w-full bg-gray-800 border border-amber-500/40 text-white text-xs rounded-lg px-2.5 py-2 pr-8 focus:outline-none focus:ring-1 focus:ring-amber-500 placeholder-gray-500" />
          <button onClick={() => setShowPwd(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">{showPwd ? <EyeOff size={11} /> : <Eye size={11} />}</button>
        </div>
      </div>
      <div className="flex gap-1.5">
        <button onClick={saveCredentials} disabled={saving}
          className="flex-1 flex items-center justify-center gap-1 bg-amber-500 hover:bg-amber-400 text-white font-bold py-2 rounded-xl text-xs transition-all">
          {saving ? '...' : <><Save size={10} /> Sauvegarder</>}
        </button>
        <button onClick={autoLogin} disabled={logging || !username || !password}
          data-testid="auto-login-btn"
          className="flex-1 flex items-center justify-center gap-1 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold py-2 rounded-xl text-xs transition-all">
          {logging ? <><div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" /> Connexion...</> : <><Check size={10} /> Connexion auto</>}
        </button>
      </div>
      <p className="text-amber-500 text-[10px] leading-relaxed">"Connexion auto" permet au serveur de se connecter avec vos identifiants et de charger la page authentifiée dans la fenêtre.</p>
    </div>
  );
};

// ── Add Supplier Form ──────────────────────────────────────────────────────────
const AddSupplierForm = ({ onAdded, onCancel }) => {
  const [name, setName]     = useState('');
  const [url, setUrl]       = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [color, setColor]   = useState('#3b82f6');
  const [saving, setSaving] = useState(false);
  const colors = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899'];

  const save = async () => {
    if (!name.trim() || !url.trim()) { toast.error('Nom et URL requis'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/supplier/suppliers`, { method: 'POST', headers: authHdr(), body: JSON.stringify({ name: name.trim(), url: url.trim(), username, password, color }) });
      if (!r.ok) throw new Error((await r.json()).detail);
      toast.success('Fournisseur ajouté !'); onAdded();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-2 p-3 bg-blue-900/20 border border-blue-700/50 rounded-xl">
      <p className="text-blue-400 text-xs font-bold">Nouveau fournisseur</p>
      <div className="grid grid-cols-2 gap-1.5">
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Nom"
          className="bg-gray-800 border border-blue-700/50 text-white text-xs rounded-lg px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-500" />
        <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://..."
          className="bg-gray-800 border border-blue-700/50 text-white text-xs rounded-lg px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-500" />
        <input value={username} onChange={e => setUsername(e.target.value)} placeholder="Identifiant"
          className="bg-gray-800 border border-blue-700/50 text-white text-xs rounded-lg px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-500" />
        <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Mot de passe"
          className="bg-gray-800 border border-blue-700/50 text-white text-xs rounded-lg px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-500" />
      </div>
      <div className="flex items-center gap-1.5">
        {colors.map(c => <button key={c} onClick={() => setColor(c)} className="w-5 h-5 rounded-full border-2 transition-all" style={{ backgroundColor: c, borderColor: color === c ? '#ffffff' : 'transparent' }} />)}
        <span className="text-gray-400 text-xs ml-1">Couleur</span>
      </div>
      <div className="flex gap-1.5">
        <button onClick={save} disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-bold py-2 rounded-xl text-xs">
          {saving ? '...' : 'Ajouter'}
        </button>
        <button onClick={onCancel} className="px-3 py-2 bg-gray-700 text-gray-400 rounded-xl text-xs hover:bg-gray-600">✕</button>
      </div>
    </div>
  );
};

// ── Main SupplierPanel ─────────────────────────────────────────────────────────
export const SupplierPanel = ({ onClose, onImportPrice }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [activeId, setActiveId]   = useState(null);
  const [showAdd, setShowAdd]     = useState(false);
  const [showCreds, setShowCreds] = useState(false);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/supplier/suppliers`, { headers: authHdr() })
      .then(r => r.json())
      .then(data => { setSuppliers(data); if (data.length > 0) setActiveId(data[0].id); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const activeSupplier = suppliers.find(s => s.id === activeId);

  const handleAdded = async () => {
    const r = await fetch(`${API_URL}/api/supplier/suppliers`, { headers: authHdr() });
    const data = await r.json();
    setSuppliers(data); setShowAdd(false);
    if (data.length > 0) setActiveId(data[data.length - 1].id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce fournisseur ?')) return;
    await fetch(`${API_URL}/api/supplier/suppliers/${id}`, { method: 'DELETE', headers: authHdr() });
    toast.success('Fournisseur supprimé');
    const updated = suppliers.filter(s => s.id !== id);
    setSuppliers(updated); setActiveId(updated[0]?.id || null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-md bg-gray-900 border-l border-gray-700 flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-700 shrink-0 bg-gray-800">
          <Globe size={14} className="text-blue-400 shrink-0" />
          <span className="text-white text-sm font-bold">Fournisseurs</span>

          {/* Supplier tabs */}
          <div className="flex items-center gap-1 flex-1 overflow-x-auto min-w-0">
            {loading && <span className="text-gray-400 text-xs ml-2">Chargement...</span>}
            {suppliers.map(s => (
              <button key={s.id}
                onClick={() => setActiveId(s.id)}
                style={activeId === s.id ? { borderColor: s.color, color: s.color } : {}}
                className={`shrink-0 flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border transition-all
                  ${activeId === s.id ? 'bg-gray-700 font-bold' : 'border-gray-600 text-gray-400 hover:text-gray-200 hover:border-gray-400'}`}>
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                {s.name}
              </button>
            ))}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-0.5 shrink-0">
            <button onClick={() => setShowAdd(a => !a)} title="Ajouter un fournisseur"
              data-testid="add-supplier-btn"
              className={`p-1.5 rounded-lg transition-all ${showAdd ? 'bg-blue-900/40 text-blue-400' : 'text-gray-400 hover:text-blue-400'}`}>
              <Plus size={13} />
            </button>
            {activeSupplier && (<>
              <button onClick={() => { setShowCreds(c => !c); setShowAdd(false); }} title="Identifiants"
                data-testid="supplier-creds-btn"
                className={`p-1.5 rounded-lg transition-all ${showCreds ? 'text-amber-400 bg-amber-900/30' : 'text-gray-400 hover:text-amber-400'}`}>
                <KeyRound size={13} />
              </button>
              <button onClick={() => handleDelete(activeSupplier.id)} title="Supprimer" className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg"><Trash2 size={13} /></button>
            </>)}
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-white rounded-lg"><X size={13} /></button>
          </div>
        </div>

        {/* ── Contenu principal : pas d'iframe, UX optimisée ── */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {!activeSupplier ? (
            <div className="flex flex-col items-center justify-center gap-3 h-48">
              <Globe size={36} className="text-gray-300" />
              <p className="text-gray-500 text-sm">Aucun fournisseur configuré</p>
              <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm px-4 py-2 rounded-xl">
                <Plus size={14} /> Ajouter un fournisseur
              </button>
            </div>
          ) : (
            <>
              {/* ── Accès rapide ── */}
              <div className="bg-blue-900/20 border border-blue-700/40 rounded-2xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Globe size={16} className="text-blue-400" />
                  <p className="text-blue-300 font-bold text-sm">{activeSupplier.name}</p>
                  <span className="text-blue-500 text-xs truncate max-w-[160px]">{activeSupplier.url}</span>
                </div>
                <button
                  onClick={() => window.open(
                    activeSupplier.url,
                    'fournisseur_phonelab',
                    'width=1100,height=860,left=200,top=80,scrollbars=yes,resizable=yes'
                  )}
                  data-testid="open-supplier-newtab-btn"
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-xl text-sm transition-all">
                  <ExternalLink size={15} /> Ouvrir {activeSupplier.name} (fenêtre intégrée)
                </button>
                <p className="text-blue-400 text-xs text-center">
                  Une fenêtre flottante s'ouvre à côté — naviguez sans quitter l'outil
                </p>
              </div>

              {/* ── Extracteur IA de prix ── */}
              <div className="border border-gray-700 rounded-2xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
                  <div className="flex items-center gap-2">
                    <Zap size={14} className="text-amber-500" />
                    <span className="text-white font-bold text-sm">Extracteur IA de prix</span>
                  </div>
                  <span className="text-xs text-gray-500">Collez l'URL du produit fournisseur</span>
                </div>
                <div className="p-3">
                  <PriceExtractor
                    supplierId={activeId}
                    currentPageUrl={activeSupplier.url}
                    onImportPrice={(p) => { onImportPrice?.(p); }}
                  />
                </div>
              </div>

              {/* ── Connexion automatique ── */}
              {showCreds && (
                <div className="border border-amber-500/30 rounded-2xl overflow-hidden">
                  <div className="px-4 py-3 bg-amber-900/20 border-b border-amber-500/30">
                    <p className="text-amber-400 font-bold text-sm">Identifiants fournisseur</p>
                  </div>
                  <div className="p-3">
                    <CredsPanel supplierId={activeId} supplier={activeSupplier} onReload={() => {}} />
                  </div>
                </div>
              )}

              {/* ── Ajouter fournisseur ── */}
              {showAdd && (
                <div className="border border-blue-700/50 rounded-2xl overflow-hidden">
                  <div className="p-3">
                    <AddSupplierForm onAdded={handleAdded} onCancel={() => setShowAdd(false)} />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
